// O que cada usuário enxerga no sistema conforme as permissões do token: tela ou ação sem permissão some.
// Token emitido antes da separação das permissões é resolvido em seguranca.ts (permissoes-token.ts).
import { usuarioTemAlgumaPermissao } from './seguranca';

/**
 * Permissão para ver cada tela (basta ter uma da lista). Rotas fora deste mapa ficam liberadas.
 */
export const PERMISSOES_DA_ROTA: Record<string, string[]> = {
    '/home': ['dashboard_visualizar'],
    '/relatorios': ['dashboard_visualizar'],
    '/veiculo': ['veiculo_visualizar'],
    '/locatarios': ['locatario_visualizar'],
    '/modelo-contrato': ['contrato_visualizar'],
    '/categoria': ['categoria_visualizar'],
    '/cadastros/financeiro/bancos': ['banco_visualizar'],
    '/filiais': ['filial_visualizar'],
    '/servico': ['servico_visualizar'],
    '/peca': ['peca_visualizar'],
    '/prestador-servico': ['prestadorservico_visualizar'],
    '/usuarios': ['usuario_visualizar', 'usuario_novo'],
    '/locacao': ['locacao_visualizar'],
    '/vistoria': ['vistoria_visualizar'],
    '/mapa-veiculos': ['rastreador_visualizar'],
    '/gestao-risco': ['gestao_risco_visualizar'],
    '/manutencoes': ['servicoveiculo_visualizar'],
    '/plano-manutencao': ['servicoveiculo_visualizar'],
    '/troca-de-oleo': ['trocadeoleo_visualizar'],
    '/estoque': ['estoque_visualizar'],
    '/multas': ['multa_visualizar'],
    '/pagamento': ['pagamento_visualizar'],
    '/lancamento': ['lancamento_visualizar'],
    '/notas-fiscais': ['nfse_visualizar'],
    '/relatorio-geral': ['relatorio_locacoes_visualizar', 'relatorio_veiculos_visualizar', 'relatorio_servicoveiculo_visualizar'],
    '/relatorio-financeiro': ['relatorio_despesas_visualizar', 'relatorio_receitas_visualizar'],
    '/auditoria': ['auditoria_visualizar'],
    '/afiliacao': ['afiliacao_visualizar'],
};

/** Ordem de preferência da tela de entrada: a primeira que o usuário pode abrir. */
const TELAS_DE_ENTRADA = [
    '/home',
    '/locacao',
    '/pagamento?Pendentes=true',
    '/vistoria',
    '/veiculo',
    '/locatarios',
    '/lancamento',
    '/multas',
    '/manutencoes',
    '/troca-de-oleo',
    '/mapa-veiculos',
    '/gestao-risco',
    '/estoque',
    '/modelo-contrato',
    '/relatorio-geral',
    '/relatorio-financeiro',
    '/notas-fiscais',
    '/auditoria',
    '/categoria',
    '/servico',
    '/peca',
    '/prestador-servico',
    '/plano-manutencao',
    '/usuarios',
    '/filiais',
    '/cadastros/financeiro/bancos',
];

function caminhoDe(link: string): string {
    const semQuery = (link || '').split('?')[0].split('#')[0];
    return semQuery.length > 1 ? semQuery.replace(/\/+$/, '') : semQuery;
}

export function podeAcessarRota(token: string, link?: string): boolean {
    if (!link) return true;
    const exigidas = PERMISSOES_DA_ROTA[caminhoDe(link)];
    return !exigidas || usuarioTemAlgumaPermissao(token, exigidas);
}

/** Primeira tela que o usuário pode abrir, ou null se não pode abrir nenhuma. */
export function telaInicial(token: string): string | null {
    return TELAS_DE_ENTRADA.find(t => podeAcessarRota(token, t)) ?? null;
}
interface ItemDeMenu {
    link?: string;
    subItems?: ItemDeMenu[];
}

/** Remove do menu o que o usuário não pode abrir; grupos que ficam vazios somem junto. */
export function filtrarMenu<T extends ItemDeMenu>(itens: T[], token: string): T[] {
    return itens.reduce<T[]>((acc, item) => {
        if (item.subItems) {
            const filhos = filtrarMenu(item.subItems as T[], token);
            if (filhos.length > 0 || item.link) acc.push({ ...item, subItems: filhos });
        } else if (podeAcessarRota(token, item.link)) {
            acc.push(item);
        }
        return acc;
    }, []);
}

/** Ações restritas de uma listagem. Ação não informada fica como está (a API não restringe). */
export interface PermissoesCrud {
    novo?: string[];
    editar?: string[];
    excluir?: string[];
}

/** novo/editar/excluir do módulo; com mais de um prefixo, basta ter a ação em um deles. */
export function crudDe(...prefixos: string[]): PermissoesCrud {
    return {
        novo: prefixos.map(p => `${p}_novo`),
        editar: prefixos.map(p => `${p}_editar`),
        excluir: prefixos.map(p => `${p}_excluir`),
    };
}

export function podeAcao(token: string, exigidas?: string[]): boolean {
    return !exigidas || usuarioTemAlgumaPermissao(token, exigidas);
}

/**
 * Tira de uma lista de ações (menu da linha ou ações em lote) as que o usuário não pode executar. A chave é o
 * texto da ação (Texto ou titulo); ação ausente do mapa fica como está.
 */
export function ocultarAcoes<T extends { Texto?: string; titulo?: string; SubMenu?: T[] }>(acoes: T[], permitidas: Record<string, boolean>): T[] {
    return acoes.reduce<T[]>((acc, a) => {
        if (permitidas[(a.Texto ?? a.titulo) as string] === false) return acc;
        if (a.SubMenu) {
            // Submenu que fica vazio some junto (ex.: "Financeiro" para quem só consulta).
            const filhos = ocultarAcoes(a.SubMenu, permitidas);
            if (filhos.length === 0) return acc;
            acc.push({ ...a, SubMenu: filhos });
            return acc;
        }
        acc.push(a);
        return acc;
    }, []);
}
